import { useLazyQuery } from '@apollo/client';
import { IonCol, IonGrid, IonInfiniteScroll, IonInfiniteScrollContent, IonRow, IonText } from '@ionic/react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateStamp from '@components/dates/DateStamp';
import { Filters } from '@components/forms/ExpertKPIsFilters';
import HandleQuery from '@components/HandleQuery';
import Tag from '@components/labels/Tag';
import Nbsp from '@components/Nbsp';
import styles from '@components/SurveyResponses.module.css';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import {
  GetSurveyResponsesDocument,
  GetSurveyResponsesQuery,
  GetSurveyResponsesQueryVariables,
  SurveyResponseServiceEnum
} from '@typing/Generated';
import { imageUrl } from '@utils/imageUtils';
import { calculateDatesFromTimePeriod } from '@utils/modelUtils';

type Props = {
  filters: Filters;
};

const SurveyResponses = ({ filters }: Props) => {
  const intl = useIntl();
  const [getSurveyResponses, getSurveyResponsesQuery] = useLazyQuery<
    GetSurveyResponsesQuery,
    GetSurveyResponsesQueryVariables
  >(GetSurveyResponsesDocument);
  const myExpert = useMyExpert();

  const surveyResponses = useNodesFromConnection(getSurveyResponsesQuery.data?.surveyResponses);

  useEffect(() => {
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    getSurveyResponses({
      variables: {
        endDate: dates.endDate,
        expertId: filters.expertId,
        service: SurveyResponseServiceEnum.typeform,
        startDate: dates.startDate
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, getSurveyResponses, myExpert]);

  async function searchNext(event: CustomEvent<void>) {
    await getSurveyResponsesQuery.fetchMore({
      variables: { cursor: getSurveyResponsesQuery.data?.surveyResponses.pageInfo?.endCursor ?? '' }
    });

    (event.target as HTMLIonInfiniteScrollElement).complete();
  }

  return (
    <HandleQuery query={getSurveyResponsesQuery}>
      {data => (
        <div>
          {surveyResponses.length === 0 && (
            <div className={styles.none}>
              <FormattedMessage id="dictionary.none" />
              <Nbsp />
            </div>
          )}
          {surveyResponses.map(survey => (
            <IonGrid className={styles.survey} key={survey.id}>
              <IonRow className="ion-align-items-start">
                <IonCol className={styles.firstCol} size="2">
                  <IonText className="bold">{survey.client.fullName}</IonText>
                </IonCol>
                <IonCol size="auto">
                  <div
                    className={styles.filledInStars}
                    style={{
                      backgroundImage: `linear-gradient(to right,  #6ab7d0 0%,#6ab7d0 ${
                        ((survey.score ?? 0) / 5) * 100
                      }%,#eaeaee 0%,#eaeaee 100%)`,
                      width: 140
                    }}
                  />
                  <img className={styles.fiveStarImage} src={imageUrl('fiveStars')} />
                </IonCol>
                <IonCol size="auto">
                  <div className={styles.score}>{survey.score}</div>
                </IonCol>
                <IonCol className="ion-margin-start" size="auto">
                  <Tag
                    color={survey.service === SurveyResponseServiceEnum.delighted ? 'error500' : 'secondary500'}
                    text={intl.formatMessage({ id: `models.surveyServices.${survey.service}` })}
                  />
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol className={styles.firstCol} size="2">
                  <IonText className={styles.light}>
                    <DateStamp value={survey.submittedAt} />
                  </IonText>
                </IonCol>
                <IonCol>{survey.textAnswers}</IonCol>
              </IonRow>
            </IonGrid>
          ))}
          <div className={styles.loadMore}>
            {data.surveyResponses.pageInfo.hasNextPage && (
              <IonInfiniteScroll
                disabled={!data.surveyResponses.pageInfo.hasNextPage}
                onIonInfinite={async (e: CustomEvent<void>) => searchNext(e)}
                threshold="100px"
              >
                <IonInfiniteScrollContent />
              </IonInfiniteScroll>
            )}
          </div>
        </div>
      )}
    </HandleQuery>
  );
};

export default SurveyResponses;
